import { useEffect, useRef, useState, type CSSProperties } from "react";

const SETTLE_MS = 3500;
/** Max stacked fades; past this the oldest (already near-opaque) is dropped. */
const MAX_OVERLAYS = 8;

export interface WashVars extends CSSProperties {
  ["--c-topic"]: string;
  ["--c-priority"]: string;
  ["--c-custom"]: string;
  ["--custom-area"]: number;
  /** V2 (modern) skin only: priority border color + contrast ink over the topic band. */
  ["--c-border"]?: string;
  ["--v2-ink"]?: string;
}

const sigOf = (v: WashVars) => `${v["--c-topic"]}|${v["--c-priority"]}|${v["--c-custom"]}`;

interface Overlay { vars: WashVars; token: number }

/** Crossfading wash stack. Gradient colors can't CSS-transition, so a color
 *  change fades a fresh wash layer in over the current state. Layers STACK:
 *  a change mid-fade adds a new overlay on top of the still-fading one instead
 *  of remounting it (the old single-overlay restart visibly snapped the
 *  half-faded color away — rapid selections made the background "jump").
 *  Only the LAST change's timer commits: it bakes the newest colors into the
 *  base and clears the stack, which is seamless because by then the top
 *  overlay sits at opacity 1. */
export function WashStack({ vars, animOn }: { vars: WashVars; animOn: boolean }) {
  const sig = sigOf(vars);
  const [base, setBase] = useState(vars);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const tokenRef = useRef(0);
  // Sig currently shown on top of the stack (newest overlay, else base).
  const liveSig = useRef(sig);

  useEffect(() => {
    if (!animOn) { liveSig.current = sig; setBase(vars); setOverlays([]); return; }
    if (sig === liveSig.current) return;
    liveSig.current = sig;
    tokenRef.current += 1;
    const tok = tokenRef.current;
    setOverlays((os) => [...os.slice(1 - MAX_OVERLAYS), { vars, token: tok }]);
    const id = setTimeout(() => {
      setBase(vars);
      setOverlays((os) => os.filter((o) => o.token > tok));
    }, SETTLE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, animOn]);

  if (!animOn) return <div className="card-wash" style={vars} />;
  return (
    <div className="wash-stack">
      <div className="card-wash" style={base} />
      {overlays.map((o) => (
        <div
          key={o.token}
          className="card-wash wash-top"
          style={{ ...o.vars, animationDuration: `${SETTLE_MS}ms` }}
        />
      ))}
    </div>
  );
}
